import { UserIcon } from "@xola/icons";
import { Alert, flash, Label, Modal, SubmitButton } from "@xola/ui-kit";
import { AxiosError } from "axios";
import clsx from "clsx";
import {
    handleSuccessfulSubmission,
    isAllDay,
    isCanceled,
    isEquipmentBased,
    isEquipmentItem,
    isPackagePurchase,
} from "common";
import { escapeRegExp, isArray, sortBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    Arrival,
    Experience,
    ExperiencePurchaseItem,
    ExperienceStatus,
    Purchase,
    PurchaseItem,
    PurchaseItemPrivacy,
    PurchaseRuleFilterErrorCode,
    ScheduleType,
} from "types";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { useAutoFetchCollection } from "../../hooks/useAutoFetchCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { hasPrivateOption, hasPublicOption, hasTimeScheduled, isVirtualExperience } from "../../models/Experience";
import { getFirstAvailableExperiencePurchaseItem } from "../../models/PurchaseItem";
import { getPurchaseItemPrivacyForTimeSlot, hasOverlappingSharedResources } from "../../models/Timeslot";
import { useRosterDrawerStore } from "../../stores/rosterDrawerStore";
import { NotifyCustomerCheckbox } from "../Common/NotifyCustomerCheckbox";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { SearchInput } from "../Common/SearchInput";
import { SecondaryButton } from "../Common/SecondaryButton";
import { ArrivalPicker } from "../DatePicker/ArrivalPicker";
import { Loader } from "../Loader";
import { ExperienceSummaryTile } from "../Product/ExperienceSummaryTile";
import { AddOnLimitOverrideSwitch } from "../PurchaseModification/AddOnLimitOverrideSwitch";
import { PaymentBreakdown } from "../PurchaseModification/PaymentBreakdown";
import { PurchaseItemSelect } from "../PurchaseModification/PurchaseItemSelect";
import { PurchaseItemSummaryTile } from "../PurchaseModification/PurchaseItemSummaryTile";
import {
    getSelectedPickupLocations,
    getUnavailablePickupLocations,
    PickupLocationsUnavailableModal,
    UnavailablePickupLocation,
} from "./PickupLocationsUnavailableModal";
import { useForms } from "@/hooks/resources/useForms";
import { useAddOnLimitOverride } from "@/hooks/useAddOnLimitOverride";
import { useVisibility } from "@/hooks/useVisibility";
import { getCouponLineItems } from "@/models/PurchaseItem";

interface SwitchExperienceModalProps {
    isOpen: boolean;
    onClose: (newPurchase?: Purchase, refundedItem?: PurchaseItem) => void;
    purchase: Purchase;
    item: ExperiencePurchaseItem;
    onContinue?: () => void;
}

export const SwitchExperienceModal = ({ isOpen, onClose, purchase, item, onContinue }: SwitchExperienceModalProps) => {
    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <ModalContent purchase={purchase} item={item} onContinue={onContinue} onClose={onClose} />
        </Modal>
    );
};

interface SwitchErrorData {
    errors?: Array<{ code?: string }>;
}

interface ModalContentProps {
    item: ExperiencePurchaseItem;
    purchase: Purchase;
    onClose: (newPurchase?: Purchase, refundedItem?: PurchaseItem) => void;
    onContinue?: () => void;
}

const ModalContent = ({ purchase, item, onClose, onContinue }: ModalContentProps) => {
    const invalidate = useInvalidate();

    const [notifyCustomer, setNotifyCustomer] = useState(true); // TODO: Use in payload when API changes are done.
    const [search, setSearch] = useState("");
    const [selectedItem, setSelectedItem] = useState<PurchaseItem>(
        getFirstAvailableExperiencePurchaseItem(purchase, item),
    );
    const [opacity, setOpacity] = useState(0);
    const [switchToExperience, setSwitchToExperience] = useState<Experience | null>(null);
    const [isCouponErrorVisible, { show: showCouponError, hide: hideCouponError }] = useVisibility(false);
    const addOnLimitOverride = useAddOnLimitOverride();
    const [pickupWarning, setPickupWarning] = useState<{
        experience: Experience;
        unavailableLocations: UnavailablePickupLocation[];
    } | null>(null);
    const seller = useSeller();
    const [arrival, setArrival] = useState<Arrival>({ date: item.arrivalDate, time: item.arrivalTime ?? null });

    const setDrawerClose = useRosterDrawerStore((state) => state.setClose);
    const { data: forms } = useForms();

    useEffect(() => {
        if (!opacity) {
            setTimeout(() => {
                setOpacity(1);
            }, 150);
        }
    }, [opacity, setOpacity]);

    const { data: experiences, isLoading: isProductCollectionLoading } =
        useAutoFetchCollection<Experience>("experiences");

    const updatedPurchase = useMemo(() => {
        return {
            items: [
                {
                    id: selectedItem.id,
                    product: { id: switchToExperience?.id },
                    arrivalDate: arrival.date,
                    arrivalTime: arrival.time,
                    ...(switchToExperience &&
                        isVirtualExperience(switchToExperience) && { arrivalTimezoneName: seller.timezoneName }),
                    ...addOnLimitOverride.itemOverride,
                },
            ],
            _emailOverrides: { sendEmailToTraveler: notifyCustomer },
        };
    }, [
        selectedItem,
        switchToExperience,
        arrival,
        notifyCustomer,
        seller.timezoneName,
        addOnLimitOverride.itemOverride,
    ]);

    const {
        mutateAsync: switchProduct,
        isLoading: isUpdating,
        isSuccess,
    } = useMutation(async () => {
        const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, updatedPurchase);
        setDrawerClose();
        if (item?.event?.id) {
            invalidate("events", item.event.id);
        }

        handleSuccessfulSubmission(() => onClose(data, selectedItem));
        onContinue?.();
    });

    const handleSubmit = async () => {
        try {
            await switchProduct();
        } catch (error) {
            if (addOnLimitOverride.handleError(error)) {
                return;
            }
            flash.danger("Error switching experience");
        }
    };

    const handleItemChange = (item: PurchaseItem) => {
        addOnLimitOverride.reset();
        setOpacity(0);
        setTimeout(() => {
            setSelectedItem(item);
        }, 150);
    };

    const handleCancelClick = () => {
        if (switchToExperience) {
            addOnLimitOverride.reset();
            setSwitchToExperience(null);
        } else {
            onClose();
        }
    };

    const handleArrivalChange = (value: Arrival) => {
        addOnLimitOverride.reset();
        setArrival(value);
    };

    const updateExperienceToSwitchWithArrival = (experience: Experience) => {
        addOnLimitOverride.reset();
        setSwitchToExperience(experience);
        setArrival({ date: arrival.date, time: isAllDay(experience) ? null : arrival.time ?? null });
    };

    const handleExperienceClick = (experience: Experience) => {
        const selectedPickupLocations = getSelectedPickupLocations(item);

        if (selectedPickupLocations.length > 0) {
            const unavailable = getUnavailablePickupLocations(forms, experience.id, selectedPickupLocations);

            if (unavailable.length > 0) {
                setPickupWarning({ experience, unavailableLocations: unavailable });
                return;
            }
        }

        updateExperienceToSwitchWithArrival(experience);
    };

    const handlePickupWarningBack = () => setPickupWarning(null);

    const handlePickupWarningProceed = () => {
        if (!pickupWarning) return;

        updateExperienceToSwitchWithArrival(pickupWarning.experience);
        setPickupWarning(null);
    };

    if (isProductCollectionLoading) {
        return (
            <Modal.Body>
                <Loader />
            </Modal.Body>
        );
    }
    const isEquipment = isEquipmentItem(selectedItem);

    const currentExperience = experiences.find((experience) => experience.id === selectedItem.product.id);
    const getSwitchablePurchaseItems = () => {
        return purchase.items.filter((item) => {
            const experience = experiences.find((experience) => {
                return experience.id === item.product?.id;
            });
            return experience && !isCanceled(item);
        });
    };

    const filteredExperiences = experiences
        .filter((experience) => experience.id !== selectedItem.product.id)
        .filter((experience) => experience.status !== ExperienceStatus.DRAFT)
        .filter((experience) => {
            if (!isEquipment && isEquipmentBased(experience)) {
                return false;
            } else if (isEquipment && !isEquipmentBased(experience)) {
                return false;
            } else {
                return true;
            }
        })
        .filter((experience) => {
            // Filter by privacy.

            // checks if the original experience has both public & private types
            const isCurrentAllowedAllPrivacy =
                currentExperience && hasPrivateOption(currentExperience) && hasPublicOption(currentExperience);

            // checks if the original experience was chosen as private type
            const isPrivateByChoice = selectedItem.privacy === PurchaseItemPrivacy.PRIVATE;

            // checks if the experience has public types only
            const isPublic = experience.allowedPrivacies.length === 1 && hasPublicOption(experience);

            if (isCurrentAllowedAllPrivacy && isPrivateByChoice && isPublic) {
                return false;
            }

            return true;
        })
        .filter((experience) => {
            // Filter by search term.
            return search.length > 0 ? new RegExp(escapeRegExp(search), "i").test(experience.name) : experience;
        });

    const sortedExperiences = sortBy(filteredExperiences, "name");

    const isValid = () => {
        if (switchToExperience) {
            return isAllDay(switchToExperience) ? true : arrival.time !== null;
        }

        return false;
    };

    const handlePaymentBreakdownError = (error: AxiosError) => {
        const validationErrors = (error.response?.data as SwitchErrorData | undefined)?.errors;
        const hasCouponLineItems = getCouponLineItems(item).length > 0;
        let inFilterNotMetError;
        if (isArray(validationErrors) && validationErrors.length > 0) {
            inFilterNotMetError = validationErrors.find(
                (error_) => error_?.code === PurchaseRuleFilterErrorCode.IN_FILTER_NOT_MET,
            );
        }

        if (!isPackagePurchase(purchase) && hasCouponLineItems && inFilterNotMetError) {
            showCouponError();
        }
    };

    const handlePaymentBreakdownUpdate = () => {
        hideCouponError();
    };

    // allow overlap if both Experiences share common resource, as resource will be freed from current experience after switch
    const hasOverlappingEventShareResources =
        currentExperience && switchToExperience
            ? hasOverlappingSharedResources(currentExperience, switchToExperience)
            : false;

    return (
        <>
            <Modal.Header>Switch Experience</Modal.Header>
            <Modal.Body>
                {/* TODO: Consider extracting the "disable" logic into `FormModal` and control it via prop. */}
                {isCouponErrorVisible && (
                    <Alert color="danger">
                        Please check if restrictions from any applied coupon are being violated and remove the coupon to
                        proceed.
                    </Alert>
                )}
                {getSwitchablePurchaseItems().length > 0 ?? <Label>Select Reservation</Label>}
                <div className="mb-4 w-full">
                    <PurchaseItemSelect
                        items={getSwitchablePurchaseItems()}
                        value={selectedItem}
                        className="flex-1"
                        onChange={handleItemChange}
                    />
                </div>
                <fieldset
                    disabled={isUpdating}
                    className={clsx(
                        "flex min-w-[100%] space-x-4 delay-150 ease-in-out",
                        { "opacity-0": opacity === 0 },
                        { "opacity-100": opacity === 1 },
                    )}
                >
                    {/* Split From/To as 50% because listing names can be big */}
                    <div className="w-1/2 space-y-4">
                        <h3>Switch From</h3>
                        <PurchaseItemSummaryTile purchase={purchase} item={selectedItem} />

                        {switchToExperience ? (
                            <div className="rounded-lg bg-gray-lighter p-6">
                                <PaymentBreakdown purchase={purchase} item={selectedItem} />
                            </div>
                        ) : null}
                    </div>

                    <div className="w-1/2 space-y-4">
                        <h3>Switch To</h3>

                        {switchToExperience ? (
                            <>
                                <div className="flex rounded-lg bg-gray-lighter p-4">
                                    <ProductImage size="small" product={switchToExperience} />

                                    <div className="overflow-x-hidden">
                                        <NoTranslate as="div" className="truncate font-bold">
                                            {switchToExperience.name}
                                        </NoTranslate>

                                        <div className="text-gray-darker">
                                            <UserIcon className="mr-1" /> {selectedItem.quantity} Guests
                                        </div>
                                    </div>
                                </div>
                                {switchToExperience?.scheduleType !== ScheduleType.OPEN && (
                                    <ArrivalPicker
                                        allowOverlap={hasOverlappingEventShareResources}
                                        timezone={
                                            isVirtualExperience(switchToExperience) ? seller.timezoneName : undefined
                                        }
                                        product={switchToExperience.id}
                                        value={arrival}
                                        showAddNewTimeSlot={hasTimeScheduled(switchToExperience)}
                                        quantity={0} // get all timeslots for switch experience even if it has 0 capacity
                                        item={item}
                                        privacyPref={getPurchaseItemPrivacyForTimeSlot(
                                            selectedItem.privacy,
                                            switchToExperience?.allowedPrivacies,
                                        )}
                                        placement="bottom"
                                        onChange={handleArrivalChange}
                                    />
                                )}

                                <div className="rounded-lg bg-gray-lighter p-6">
                                    <PaymentBreakdown
                                        isEnabled={!isSuccess}
                                        purchase={purchase}
                                        item={selectedItem}
                                        payload={updatedPurchase}
                                        onError={handlePaymentBreakdownError}
                                        onAddOnLimitError={addOnLimitOverride.handleError}
                                        onUpdate={handlePaymentBreakdownUpdate}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <SearchInput
                                    value={search}
                                    className="mb-4"
                                    onChange={(searchResult: string) => setSearch(searchResult)}
                                />

                                <div className="max-h-72 divide-y divide-gray-lighter overflow-y-auto overflow-x-hidden">
                                    {sortedExperiences?.map((experience) => (
                                        <div key={experience.id}>
                                            <ExperienceSummaryTile
                                                className="cursor-pointer rounded-none !bg-white hover:bg-gray-lighter"
                                                experience={experience}
                                                onClick={() => handleExperienceClick(experience)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </fieldset>
                <fieldset disabled={isUpdating} className="mt-4">
                    <AddOnLimitOverrideSwitch limitOverride={addOnLimitOverride} />
                </fieldset>
            </Modal.Body>

            <Modal.Footer className="flex items-center justify-end space-x-4">
                {switchToExperience ? (
                    <NotifyCustomerCheckbox
                        isChecked={notifyCustomer}
                        onChange={(e) => setNotifyCustomer(e.target.checked)}
                    />
                ) : null}

                <SecondaryButton disabled={isUpdating} onClick={handleCancelClick}>
                    Cancel
                </SecondaryButton>

                {switchToExperience ? (
                    <SubmitButton
                        disabled={!isValid() || addOnLimitOverride.isAddonLimitExceeded}
                        isLoading={isUpdating}
                        isSuccess={isSuccess}
                        onClick={handleSubmit}
                    >
                        Confirm Switch
                    </SubmitButton>
                ) : null}
            </Modal.Footer>

            <PickupLocationsUnavailableModal
                isOpen={!!pickupWarning}
                sourceExperienceName={currentExperience?.name ?? ""}
                targetExperienceName={pickupWarning?.experience.name ?? ""}
                unavailableLocations={pickupWarning?.unavailableLocations ?? []}
                onBack={handlePickupWarningBack}
                onProceed={handlePickupWarningProceed}
            />
        </>
    );
};
